import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import * as FileSaver from "file-saver";
import { map, tap } from "rxjs/operators";
import { Spinner } from "src/app/core/services/spinner.service";
import { RequestHelper, UtilitiesService } from "src/app/core/services/utilities.service";
import { GetAllResponse, SingleResponse } from "src/app/shared/model/restful-api.model";
import { ExcelService } from "src/app/shared/service/excel.service";
import { DateUtil } from "src/app/shared/utility/date.util";
import { environment } from "src/environments/environment";
import { Document } from "../models/domain/document.model";
import { DocumentFormValue } from "../models/form/document-form-value.model";
import { JPAPage } from "../models/pay-load/jpa.model";
import { AddCommentReq } from "../models/pay-load/request/comment.request";
import { FilterDocumentsReq } from "../models/pay-load/request/filter-document-req.model";
import { DashBoardRes } from "../models/pay-load/response/chart.response";
import {
  AllMasterDataResponse,
  CommodityTier3Res,
  DocumentItemResponse,
  OwnerProfile,
  UploadingAttachmentRes
} from "../models/pay-load/response/response.model";

@Injectable({
  providedIn: "root"
})
export class DocStoreService {
  public static readonly DOCSTORE_MODULE_BASE_URL = environment.docStoreURL;
  private requestHelper: RequestHelper;
  constructor(
    private utilService: UtilitiesService,
    private excelService: ExcelService,
    private httpClient: HttpClient,
    private spinner: Spinner
  ) {
    this.requestHelper = utilService.req;
  }

  fetchDocumentList(documentListRequest: FilterDocumentsReq) {
    const httpParams = this.createQueryParamsFromContrastListRequest(documentListRequest);
    return this.requestHelper
      .httpGet<SingleResponse<JPAPage<DocumentItemResponse>>>(
        DocStoreService.DOCSTORE_MODULE_BASE_URL + "/docstore/search?" + httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  downloadDocuments(documentListRequest: FilterDocumentsReq) {
    this.spinner.show();
    const httpParams = this.createQueryParamsFromContrastListRequest(documentListRequest);
    const url =
      DocStoreService.DOCSTORE_MODULE_BASE_URL + "/docstore/export?" + httpParams.toString();
    this.httpClient.get(url, { responseType: "arraybuffer" }).subscribe(fileData => {
      const fileName = "DocumentRepository";
      this.excelService.saveAsExcelFile(fileData, fileName);
      this.spinner.hide();
    });
  }

  getDashBoardData(request: FilterDocumentsReq) {
    const httpParams = this.createQueryParamsFromContrastListRequest(request);
    return this.requestHelper
      .httpGet<SingleResponse<DashBoardRes>>(
        DocStoreService.DOCSTORE_MODULE_BASE_URL +
        "/docstore/dashboard/get?" +
        httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  private createQueryParamsFromContrastListRequest(documentListRequest: FilterDocumentsReq) {
    let httpParams = new HttpParams();
    Object.keys(documentListRequest).forEach(function (key) {
      const isNullKey = documentListRequest[key] == null;
      if (isNullKey) {
        return;
      }

      const isEmptyStringKey =
        typeof documentListRequest[key] == "string" && documentListRequest[key].trim().length === 0;
      if (isEmptyStringKey) {
        return;
      }

      const isEmptyArrayKey =
        Array.isArray(documentListRequest[key]) && documentListRequest[key].length === 0;
      if (isEmptyArrayKey) {
        return;
      }

      const isKeyDateType = documentListRequest[key] instanceof Date;
      if (isKeyDateType) {
        httpParams = httpParams.append(
          key,
          "" + DateUtil.parseDateToTimestamp(documentListRequest[key])
        );
        return;
      }

      const isStringKey = typeof documentListRequest[key] === "string";
      if (isStringKey) {
        httpParams = httpParams.append(key, documentListRequest[key].trim());
        return;
      }

      httpParams = httpParams.append(key, documentListRequest[key]);
    });
    return httpParams;
  }

  fetchDocument(documentID: string) {
    return this.requestHelper
      .httpGet<SingleResponse<Document>>(
        DocStoreService.DOCSTORE_MODULE_BASE_URL + "/docstore/getById/" + documentID
      )
      .pipe(map(response => response.data));
  }

  fetchAllMasterData() {
    return this.requestHelper
      .httpGet<SingleResponse<AllMasterDataResponse>>(
        DocStoreService.DOCSTORE_MODULE_BASE_URL + "/docstore/master-data/getAll"
      )
      .pipe(map(response => response.data));
  }

  fetchSupplierNamesByKeyword(keyword: string) {
    const httpParams: HttpParams = new HttpParams().append("keyword", keyword);
    return this.requestHelper
      .httpGet<SingleResponse<string[]>>(
        DocStoreService.DOCSTORE_MODULE_BASE_URL +
        "/docstore/master-data/getSupplierName?" +
        httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  fetchGSCByKeyword(keyword: string) {
    const httpParams: HttpParams = new HttpParams().append("keyword", keyword);
    return this.requestHelper
      .httpGet<SingleResponse<string[]>>(
        DocStoreService.DOCSTORE_MODULE_BASE_URL +
        "/docstore/master-data/getSupplierGSC?" +
        httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  fetchSCXByKeyword(keyword: string) {
    const httpParams: HttpParams = new HttpParams().append("keyword", keyword);
    return this.requestHelper
      .httpGet<SingleResponse<string[]>>(
        DocStoreService.DOCSTORE_MODULE_BASE_URL +
        "/docstore/master-data/getSupplierSCX?" +
        httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  fetchCommodityTier3ByKeyword(keyword: string) {
    const httpParams: HttpParams = new HttpParams().append("keyword", keyword);
    return this.requestHelper
      .httpGet<SingleResponse<CommodityTier3Res[]>>(
        DocStoreService.DOCSTORE_MODULE_BASE_URL +
        "/docstore/master-data/getCommodityTier3?" +
        httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  fetchDocumentOwnersByKeyword(keyword: string) {
    const httpParams: HttpParams = new HttpParams().append("keyword", keyword);
    return this.requestHelper
      .httpGet<GetAllResponse<OwnerProfile>>(
        DocStoreService.DOCSTORE_MODULE_BASE_URL +
        "/docstore/user/getContractOwner?" +
        httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  getDocumentOwnersByKeywordAsValueAndText(keyword: string) {
    return this.fetchDocumentOwnersByKeyword(keyword).pipe(
      map(ownerProfiles => {
        return ownerProfiles.map(ownerProfile => ({
          value: ownerProfile.sso,
          text: ownerProfile.userName
        }));
      })
    );
  }

  fetchAllCommodityTier3s() {
    const httpParams: HttpParams = new HttpParams().append("keyword", "");
    return this.requestHelper
      .httpGet<SingleResponse<CommodityTier3Res[]>>(
        DocStoreService.DOCSTORE_MODULE_BASE_URL +
        "/docstore/master-data/getCommodityTier3?" +
        httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  saveDocument(documentFormValue: DocumentFormValue) {
    const requestBody = documentFormValue.toDocumentDTO();
    return this.requestHelper
      .httpPut<SingleResponse<number>>(
        DocStoreService.DOCSTORE_MODULE_BASE_URL + "/docstore/save",
        requestBody
      )
      .pipe(map(response => response.data));
  }

  saveDocumentAsDraft(documentFormValue: DocumentFormValue) {
    const requestBody = documentFormValue.toDocumentDTO();
    return this.requestHelper
      .httpPut<SingleResponse<number>>(
        DocStoreService.DOCSTORE_MODULE_BASE_URL + "/docstore/saveDraft",
        requestBody
      )
      .pipe(map(response => response.data));
  }

  addComment(requestBody: AddCommentReq) {
    return this.requestHelper
      .httpPost<SingleResponse<void>>(
        DocStoreService.DOCSTORE_MODULE_BASE_URL + "/docstore/comment/add",
        requestBody
      )
      .pipe(map(response => response.data));
  }

  uploadDocumentAttachment(file: File) {
    const formData = new FormData();
    formData.append("file", file, file.name);
    return this.requestHelper.httpPost<UploadingAttachmentRes>(
      DocStoreService.DOCSTORE_MODULE_BASE_URL + "/docstore/document/upload",
      formData
    );
  }

  downloadAttachmentByS3FileIdAndFileName(s3FileId: string, fileName: string) {
    this.spinner.show();
    let httpParams = new HttpParams().append("fileId", s3FileId).append("fileName", fileName);
    const url =
    DocStoreService.DOCSTORE_MODULE_BASE_URL +
      "/docstore/document/download?" +
      httpParams.toString();
    const fileData$ = this.httpClient
      .post(url, null, { observe: "response", responseType: "arraybuffer" })
      .pipe(map(httpResponse => httpResponse.body));
    return fileData$.pipe(
      tap(fileData => {
        const file = new Blob([fileData], {
          type: "application/octet-stream"
        });
        FileSaver.saveAs(file, fileName);
        this.spinner.hide();
      }),
      map(() => {
        return;
      })
    );
  }

  downloadAttachmentsByDocumentId(documentId: string, requestNumber: string): void {
    this.spinner.show();
    this.httpClient
      .post(
        DocStoreService.DOCSTORE_MODULE_BASE_URL + "/docstore/document/downloadAll/" + documentId,
        null,
        { observe: "response", responseType: "arraybuffer" }
      )
      .pipe(map(httpResponse => httpResponse.body))
      .subscribe(fileData => {
        const file = new Blob([fileData], {
          type: "application/zip"
        });
        FileSaver.saveAs(file, requestNumber + "-" + "Attachments");
        this.spinner.hide();
      });
  }

  terminateDocument(documentId: string) {
    return this.requestHelper.httpDelete<void>(
      DocStoreService.DOCSTORE_MODULE_BASE_URL + "/docstore/terminate/" + documentId
    );
  }

  reOpenDocument(documentId: string) {
    return this.requestHelper.httpPut<void>(
      DocStoreService.DOCSTORE_MODULE_BASE_URL + "/docstore/reopen/" + documentId
    );
  }

  migrateDocuments(smartSheetFile: File) {
    const formData = new FormData();
    formData.append("fileSmartSheet", smartSheetFile, smartSheetFile.name);
    return this.requestHelper.httpPut<UploadingAttachmentRes>(
      DocStoreService.DOCSTORE_MODULE_BASE_URL + "/docstore/saveMass",
      formData
    );
  }

  delegate(ownerSSO: string, newOwnerSSO: string) {
    const url = DocStoreService.DOCSTORE_MODULE_BASE_URL + "/docstore/delegate";
    const payload = {
      ownerFrom: ownerSSO,
      ownerTo: newOwnerSSO
    };
    return this.httpClient.put<void>(url, null, { params: payload });
  }

  searchSupplierInfo(keyword: string) {
    return this.httpClient.get(DocStoreService.DOCSTORE_MODULE_BASE_URL + '/docstore/supplier?keyword=' + keyword);
  }
}
